import { useQuery, type QueryStatus } from "@tanstack/react-query";
import { BarChart3 } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { LockedContent } from "@/components/layout/locked-content";

import { message } from "@/lib/utils";
import { chartBgColors } from "@/lib/chart";
import type { ShareOfVoiceAPIData } from "@/schema/brand-os/brand-intel";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";

function BarContent({ data }: { data: ShareOfVoiceAPIData }) {
  return (
    <div className="space-y-6">
      {data.entries.map((entry, index) => (
        <div key={entry.brand_name} className="flex items-center gap-3">
          <p className="w-20 shrink-0 truncate text-sm font-medium text-foreground">{entry.brand_name}</p>
          <div className="relative min-w-0 flex-1">
            <Progress value={Math.min(100, entry.relative_value)} className="h-2 bg-secondary" indicatorClassName={chartBgColors[index % chartBgColors.length]} />
          </div>
          <p className="w-12 shrink-0 text-right text-sm text-muted-foreground">{Math.round(entry.relative_value)}%</p>
        </div>
      ))}
    </div>
  );
};

function ShareOfVoiceContent({ status, data }: { status: QueryStatus; data: ShareOfVoiceAPIData | undefined }) {
  switch (status) {
    case "pending":
      return <ShareOfVoiceSkeleton />;
    case "error":
      return <LockedContent message={message} className="mb-10" />;
    case "success":
      if (!data?.entries?.length) return <LockedContent message={message} className="mb-10" />;
      return <BarContent data={data} />;
  }
};

export function DigitalShareVoice() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchShareOfVoice());

  return (
    <Card>
      <CardHeader className="flex flex-row items-center">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <div className="rounded-md bg-destructive/10 p-2">
            <BarChart3 className="size-5 text-destructive" strokeWidth={2.5} />
          </div>
          Digital Share of Voice
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ShareOfVoiceContent status={status} data={data} />
      </CardContent>
    </Card>
  );
};

function ShareOfVoiceSkeleton() {
  return (
    <div className="space-y-6">
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-2 flex-1 rounded-full" />
          <Skeleton className="h-4 w-12" />
        </div>
      ))}
    </div>
  );
};
